import { useState, useEffect, useMemo } from "react";
import { useAppConfig } from "../../../AppConfigProvider";
import AsyncStorage from "@react-native-async-storage/async-storage";
import VerifyEmailForm from "../../../components/auth/VerifyEmailForm/VerifyEmailForm";
import { sendEmailOTP, sendSMSOTP, verifyEmailOTP, verifySMSOTP } from "../../../services";
import { useNavigation } from "@react-navigation/native";
import { useAuth } from "../../../hooks/useAuth";
import { Platform } from "react-native";
import { announceForAccessibility } from "../../../utils/accessibility";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";

const sendEmailOTPHandler = async ({ email, setErrorMessage }) => {
  if (email === "") {
    setErrorMessage("Error sending code. Please try again.");
    return;
  }

  // Check if email is valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    setErrorMessage("Error sending code. Please try again.");
    return;
  }

  try {
    const token = await AsyncStorage.getItem("accessToken");

    await sendEmailOTP(email, token);
    setErrorMessage("");
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      setErrorMessage(error.response.data.error);
    } else {
      setErrorMessage("Error sending code. Please try again.");
    }
  }
};

const sendSMSOTPHandler = async ({ countryCode, phoneNumber, setErrorMessage }) => {
  if (phoneNumber === "") {
    setErrorMessage("Error sending code. Please try again.");
    return;
  }

  try {
    const token = await AsyncStorage.getItem("accessToken");
    await sendSMSOTP(countryCode, phoneNumber, token);
    setErrorMessage("");
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      setErrorMessage(error.response.data.error);
    } else {
      setErrorMessage("Error sending code. Please try again.");
    }
  }
};

function VerifyEmailContainer() {
  const appConfig = useAppConfig();
  const { verifyOTPForSession, isAuthenticated, isEmailVerified, otpVerifiedForSession, meData } = useAuth();
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSentTime, setLastSentTime] = useState(null);

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const domainKey = appConfig.key;
  const isCompanionMode = appConfig.isCompanionMode;

  useEffect(() => {
    if (Platform.OS === "ios" && errorMessage) {
      announceForAccessibility({ message: "Error alert" + errorMessage, queue: false });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (!meData?.me || !isAuthenticated) {
      navigation.reset({
        index: 0,
        routes: [{ name: "Login" }],
      });
    }

    if (isAuthenticated && isEmailVerified && otpVerifiedForSession) {
      navigation.reset({
        index: 0,
        routes: [isCompanionMode ? { name: "Companion Home" } : { name: "Home" }],
      });
    }

    const canResendOTP = !lastSentTime || Date.now() - lastSentTime > 60000;

    if (canResendOTP) {
      handleSendOTP();
      setLastSentTime(Date.now());
    }
  }, [meData?.me, lastSentTime]);

  const handleSendOTP = async () => {
    if (!isEmailVerified) {
      sendEmailOTPHandler({ email: meData?.me?.email, setErrorMessage });
    } else if (meData?.me?.otpPreference === "EMAIL") {
      await sendEmailOTPHandler({ email: meData?.me?.email, setErrorMessage });
    } else if (meData?.me?.otpPreference === "SMS") {
      await sendSMSOTPHandler({
        countryCode: meData?.me?.countryCode,
        phoneNumber: meData?.me?.phoneNumber,
        setErrorMessage,
      });
    }
  };

  const handleOTPChange = (text) => {
    setOtp(text);
  };

  const handleVerifyOTP = async () => {
    if (otp === "") {
      setErrorMessage("Field must be filled out");
      return;
    }

    // Check if code is valid
    const otpRegex = /^\d{6}$/;
    if (!otpRegex.test(otp)) {
      setErrorMessage("Code must be 6 digits");
      return;
    }

    try {
      const token = await AsyncStorage.getItem("accessToken");

      let newToken;
      if (meData?.me?.otpPreference === "EMAIL" || meData?.me?.otpPreference === "NONE") {
        const response = await verifyEmailOTP(meData?.me?.email, otp, token);
        newToken = response.token;
      } else if (meData?.me?.otpPreference === "SMS") {
        const response = await verifySMSOTP(meData?.me?.countryCode, meData?.me?.phoneNumber, otp, token);
        newToken = response.token;
      }

      setErrorMessage("");

      await verifyOTPForSession(newToken);

      navigation.reset({
        index: 0,
        routes: [isCompanionMode ? { name: "Companion Home" } : { name: "Home" }],
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage("Error sending code. Please try again.7");
      }
    }
  };

  const handleResendOTP = async () => {
    const canResendOTP = !lastSentTime || Date.now() - lastSentTime > 60000;

    if (!canResendOTP) {
      setErrorMessage("Please wait before requesting a new code.");
      return;
    }

    setErrorMessage("");
    await handleSendOTP();
    setLastSentTime(Date.now());
  };

  return (
    <VerifyEmailForm
      isAfterSignup={!isEmailVerified}
      otpPreference={meData?.me?.otpPreference || "EMAIL"}
      errorMessage={errorMessage}
      companionName={domainKey}
      otp={otp}
      onOTPChange={handleOTPChange}
      onVerifyOTP={handleVerifyOTP}
      onResendOTP={handleResendOTP}
    />
  );
}

export default VerifyEmailContainer;
